// @ts-nocheck
import { useEffect, useState } from 'react'
import {
  Table,
  Tag,
  Space,
  Button,
  Modal,
  Select,
  Input,
  DatePicker,
  Form,
  Descriptions,
  Steps,
  Image,
  message,
  Row,
  Col,
  Card,
  Statistic,
  Popconfirm,
} from 'antd'
import {
  EyeOutlined,
  SearchOutlined,
  ExportOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  TruckOutlined,
  DollarOutlined,
  ShoppingOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { orderAPI, ASSETS_BASE_URL } from '../../api'
import { confirmCancel } from '../../hooks/useConfirm'

const { Option } = Select
const { RangePicker } = DatePicker
const { TextArea } = Input

interface OrderItem {
  id: number
  productName: string
  spec: string
  quantity: number
  price: number
  isGift: boolean
}

interface Order {
  id: number
  orderNumber: string
  customerId: number
  customerName: string
  customerPhone: string
  storeName: string
  totalAmount: number
  discountAmount: number
  payAmount: number
  status: string
  paymentMethod: string
  paymentStatus: string
  paymentProof: string | null
  deliveryDate: string
  deliveryAddress: string
  deliveryNote: string | null
  items: OrderItem[]
  createdAt: string
  paidAt: string | null
  deliveredAt: string | null
  completedAt: string | null
  cancelledAt: string | null
  cancelReason: string | null
}

const statusMap: Record<string, { color: string; text: string; step: number }> = {
  pending: { color: 'orange', text: '待付款', step: 0 },
  paid: { color: 'blue', text: '待发货', step: 1 },
  delivered: { color: 'cyan', text: '已发货', step: 2 },
  completed: { color: 'green', text: '已完成', step: 3 },
  cancelled: { color: 'red', text: '已取消', step: -1 },
}

const paymentStatusMap: Record<string, { color: string; text: string }> = {
  pending: { color: 'orange', text: '待审核' },
  verified: { color: 'green', text: '已审核' },
  rejected: { color: 'red', text: '已驳回' },
}

const paymentMethodMap: Record<string, string> = {
  wechat: '微信支付',
  qrcode: '扫码支付',
  transfer: '银行转账',
  offline: '线下转账',
  alipay: '支付宝',
}

// Helper: ensure image URL has full API domain
const getImageUrl = (path: string) => {
  if (!path) return ''
  if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('data:')) return path
  return `${ASSETS_BASE_URL}${path.startsWith('/') ? '' : '/'}${path}`
}

const Orders = () => {
  const [orders, setOrders] = useState<Order[]>([])
  const [loading, setLoading] = useState(false)
  const [detailVisible, setDetailVisible] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null)
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [searchKeyword, setSearchKeyword] = useState('')
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null)
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 })
  const [cancelModalVisible, setCancelModalVisible] = useState(false)
  const [cancelReason, setCancelReason] = useState('')
  const [verifyModalVisible, setVerifyModalVisible] = useState(false)
  const [verifyResult, setVerifyResult] = useState<'verified' | 'rejected'>('verified')
  const [deliveryModalVisible, setDeliveryModalVisible] = useState(false)
  const [deliveringOrder, setDeliveringOrder] = useState<Order | null>(null)
  const [deliveryNote, setDeliveryNote] = useState('')
  const [deliveryForm] = Form.useForm()

  const [stats, setStats] = useState({
    totalOrders: 0,
    pendingCount: 0,
    paidCount: 0,
    todayAmount: 0,
  })

  useEffect(() => {
    refreshOrders()
  }, [filterStatus])

  useEffect(() => {
    fetchOrders()
  }, [pagination.current, pagination.pageSize])

  const fetchOrders = async (page?: number) => {
    setLoading(true)
    try {
      const params: any = {
        page: page ?? pagination.current,
        pageSize: pagination.pageSize,
      }
      if (filterStatus !== 'all') params.status = filterStatus
      if (searchKeyword) params.keyword = searchKeyword
      if (dateRange) {
        params.startDate = dateRange[0].format('YYYY-MM-DD')
        params.endDate = dateRange[1].format('YYYY-MM-DD')
      }

      const res = await orderAPI.getList(params)
      if (res.code === 200) {
        setOrders(res.data.list || [])
        setPagination(prev => ({ ...prev, total: res.data.total || 0, current: page ?? prev.current }))
        if (res.data.stats) {
          setStats(res.data.stats)
        }
      }
    } catch (error) {
      message.error('获取订单失败')
    } finally {
      setLoading(false)
    }
  }

  const refreshOrders = () => {
    fetchOrders(1)
  }

  const handleSearch = () => {
    fetchOrders(1)
  }

  const handleViewDetail = async (record: Order) => {
    try {
      const res = await orderAPI.getDetail(record.id)
      if (res.code === 200) {
        setSelectedOrder(res.data)
      } else {
        setSelectedOrder(record)
      }
    } catch (error) {
      setSelectedOrder(record)
    }
    setDetailVisible(true)
  }

  const handleCancelOrder = async () => {
    if (!cancelReason) {
      message.warning('请输入取消原因')
      return
    }
    if (!selectedOrder) return

    try {
      await orderAPI.cancel(selectedOrder.id, cancelReason)
      message.success('订单已取消')
      setCancelModalVisible(false)
      setCancelReason('')
      setSelectedOrder(null)
      refreshOrders()
    } catch (error) {
      message.error('取消订单失败')
    }
  }

  const handleVerifyPayment = async () => {
    if (!selectedOrder) return
    try {
      await orderAPI.verifyPayment(selectedOrder.id, verifyResult)
      message.success(verifyResult === 'verified' ? '审核通过' : '已驳回')
      setVerifyModalVisible(false)
      setSelectedOrder(null)
      refreshOrders()
    } catch (error) {
      message.error('审核失败')
    }
  }

  const handleDelivery = (record: Order) => {
    setDeliveringOrder(record)
    setDeliveryNote('')
    deliveryForm.resetFields()
    setDeliveryModalVisible(true)
  }

  const handleDeliverySubmit = async () => {
    if (!deliveringOrder) return

    try {
      const values = await deliveryForm.validateFields()
      await orderAPI.deliver(deliveringOrder.id, {
        logisticsCompany: '',
        trackingNumber: '',
        remark: values.remark || '',
      })
      message.success('发货成功')
      setDeliveryModalVisible(false)
      setDeliveringOrder(null)
      refreshOrders()
      fetchOrders()
    } catch (error) {
      if (error && error.errorFields) return
      message.error('发货失败')
    }
  }

  const handleExport = async () => {
    if (!dateRange) {
      message.warning('请选择日期范围')
      return
    }

    try {
      const params = {
        startDate: dateRange[0].format('YYYY-MM-DD'),
        endDate: dateRange[1].format('YYYY-MM-DD'),
        fields: [
          'orderNumber',
          'createdAt',
          'customerName',
          'totalAmount',
          'status',
          'paymentMethod',
        ],
      }
      const res = await orderAPI.export(params)

      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `订单导出_${dayjs().format('YYYYMMDD')}.xlsx`
      a.click()
      window.URL.revokeObjectURL(url)
      message.success('导出成功')
    } catch (error) {
      message.error('导出失败')
    }
  }

  const columns = [
    { title: '订单号', dataIndex: 'orderNumber', key: 'orderNumber', width: 180 },
    {
      title: '客户信息',
      key: 'customer',
      width: 180,
      render: (_: any, record: Order) => (
        <div>
          <div>{record.customerName}</div>
          <div style={{ fontSize: 12, color: '#999' }}>{record.customerPhone}</div>
        </div>
      ),
    },
    { title: '门店', dataIndex: 'storeName', key: 'storeName', width: 150 },
    {
      title: '金额',
      key: 'amount',
      width: 130,
      render: (_: any, record: Order) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>¥{record.payAmount.toFixed(2)}</div>
          {record.discountAmount > 0 && (
            <div
              style={{ fontSize: 12, color: '#999', textDecoration: 'line-through' }}
            >
              ¥{record.totalAmount.toFixed(2)}
            </div>
          )}
        </div>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (val: string) => (
        <Tag color={statusMap[val]?.color}>{statusMap[val]?.text || val}</Tag>
      ),
    },
    {
      title: '支付状态',
      dataIndex: 'paymentStatus',
      key: 'paymentStatus',
      width: 100,
      render: (val: string, record: Order) => {
        if (record.paymentMethod !== 'wechat') {
          return (
            <Tag color={paymentStatusMap[val]?.color}>
              {paymentStatusMap[val]?.text}
            </Tag>
          )
        }
        return <Tag color="green">已支付</Tag>
      },
    },
    { title: '配送日期', dataIndex: 'deliveryDate', key: 'deliveryDate', width: 120 },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (val: string) => (val ? dayjs(val).format('YYYY-MM-DD HH:mm') : '-'),
    },
    {
      title: '操作',
      key: 'action',
      width: 240,
      fixed: 'right' as const,
      render: (_: any, record: Order) => (
        <Space size="small" wrap>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
          >
            详情
          </Button>
          {record.status === 'paid' && (
            <Button
              type="link"
              size="small"
              icon={<TruckOutlined />}
              onClick={() => handleDelivery(record)}
            >
              发货
            </Button>
          )}
          {record.paymentMethod !== 'wechat' &&
            record.paymentStatus === 'pending' && (
              <Button
                type="link"
                size="small"
                onClick={() => {
                  setSelectedOrder(record)
                  setVerifyModalVisible(true)
                }}
              >
                审核
              </Button>
            )}
          {['pending', 'paid'].includes(record.status) && (
            <Popconfirm
              title="确定取消订单吗？"
              onConfirm={() => {
                setSelectedOrder(record)
                setCancelModalVisible(true)
              }}
              okText="取消订单"
              okType="danger"
              cancelText="返回"
            >
              <Button type="link" size="small" danger>
                取消
              </Button>
            </Popconfirm>
          )}
          {record.status === 'cancelled' && (
            <Popconfirm
              title="确定删除该订单？订单及明细将彻底删除，不可恢复"
              onConfirm={async () => {
                try {
                  const res = await orderAPI.delete(record.id)
                  if (res.code === 200) {
                    message.success('删除成功')
                    fetchOrders()
                  } else {
                    message.error(res.message || '删除失败')
                  }
                } catch (e: any) {
                  message.error(e?.message || '删除失败')
                }
              }}
              okText="删除"
              okType="danger"
              cancelText="返回"
            >
              <Button type="link" size="small" danger>
                删除
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ]

  return (
    <div className="orders-page">
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="总订单数"
              value={stats.totalOrders}
              prefix={<ShoppingOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="待处理"
              value={stats.pendingCount}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="待发货"
              value={stats.paidCount}
              prefix={<TruckOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="今日销售额"
              value={stats.todayAmount}
              prefix={<DollarOutlined />}
              precision={2}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input
            placeholder="订单号/客户名/手机号"
            prefix={<SearchOutlined />}
            style={{ width: 250 }}
            value={searchKeyword}
            onChange={e => setSearchKeyword(e.target.value)}
            onPressEnter={handleSearch}
          />
          <Select style={{ width: 120 }} value={filterStatus} onChange={setFilterStatus}>
            <Option value="all">全部状态</Option>
            <Option value="pending">待付款</Option>
            <Option value="paid">待发货</Option>
            <Option value="delivered">已发货</Option>
            <Option value="completed">已完成</Option>
            <Option value="cancelled">已取消</Option>
          </Select>
          <RangePicker
            value={dateRange}
            onChange={dates =>
              setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)
            }
            placeholder={['开始日期', '结束日期']}
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
            搜索
          </Button>
          <Button icon={<ExportOutlined />} onClick={handleExport}>
            导出
          </Button>
        </Space>
      </Card>

      <Card size="small">
        <Table
          columns={columns}
          dataSource={orders}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: total => `共 ${total} 条`,
            onChange: (page, pageSize) =>
              setPagination(prev => ({ ...prev, current: page, pageSize })),
          }}
          scroll={{ x: 1400 }}
        />
      </Card>

      {/* 订单详情弹窗 */}
      <Modal
        title={`订单详情 - ${selectedOrder?.orderNumber}`}
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={900}
      >
        {selectedOrder && (
          <div>
            {selectedOrder.status !== 'cancelled' && (
              <Steps
                current={statusMap[selectedOrder.status]?.step}
                style={{ marginBottom: 24 }}
                items={[
                  {
                    title: '待付款',
                    description: selectedOrder.paidAt
                      ? dayjs(selectedOrder.paidAt).format('MM-DD HH:mm')
                      : '-',
                  },
                  {
                    title: '待发货',
                    description: selectedOrder.deliveredAt
                      ? dayjs(selectedOrder.deliveredAt).format('MM-DD HH:mm')
                      : '-',
                  },
                  {
                    title: '已发货',
                    description: selectedOrder.completedAt
                      ? dayjs(selectedOrder.completedAt).format('MM-DD HH:mm')
                      : '-',
                  },
                  { title: '已完成', description: '-' },
                ]}
              />
            )}

            <Row gutter={16}>
              <Col span={12}>
                <Descriptions title="订单信息" column={1} size="small">
                  <Descriptions.Item label="订单号">
                    {selectedOrder.orderNumber}
                  </Descriptions.Item>
                  <Descriptions.Item label="客户名称">
                    {selectedOrder.customerName}
                  </Descriptions.Item>
                  <Descriptions.Item label="联系电话">
                    {selectedOrder.customerPhone}
                  </Descriptions.Item>
                  <Descriptions.Item label="所属门店">
                    {selectedOrder.storeName}
                  </Descriptions.Item>
                  <Descriptions.Item label="创建时间">
                    {dayjs(selectedOrder.createdAt).format('YYYY-MM-DD HH:mm:ss')}
                  </Descriptions.Item>
                  <Descriptions.Item label="配送日期">
                    {selectedOrder.deliveryDate}
                  </Descriptions.Item>
                  <Descriptions.Item label="配送地址">
                    {selectedOrder.deliveryAddress}
                  </Descriptions.Item>
                  {selectedOrder.deliveryNote && (
                    <Descriptions.Item label="配送备注">
                      {selectedOrder.deliveryNote}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Col>
              <Col span={12}>
                <Descriptions title="支付信息" column={1} size="small">
                  <Descriptions.Item label="商品总额">
                    ¥{selectedOrder.totalAmount.toFixed(2)}
                  </Descriptions.Item>
                  <Descriptions.Item label="优惠金额">
                    ¥{selectedOrder.discountAmount.toFixed(2)}
                  </Descriptions.Item>
                  <Descriptions.Item label="实付金额">
                    <span
                      style={{
                        fontSize: 18,
                        fontWeight: 'bold',
                        color: '#cf1322',
                      }}
                    >
                      ¥{selectedOrder.payAmount.toFixed(2)}
                    </span>
                  </Descriptions.Item>
                  <Descriptions.Item label="支付方式">
                    {paymentMethodMap[selectedOrder.paymentMethod] ||
                      selectedOrder.paymentMethod}
                  </Descriptions.Item>
                  {selectedOrder.paymentProof && (
                    <Descriptions.Item label="支付凭证">
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {selectedOrder.paymentProof.split(',').filter(Boolean).map((url: string, idx: number) => (
                          <Image key={idx} src={getImageUrl(url.trim())} width={100} style={{ borderRadius: 4 }} />
                        ))}
                      </div>
                    </Descriptions.Item>
                  )}
                  {selectedOrder.cancelReason && (
                    <Descriptions.Item label="取消原因">
                      {selectedOrder.cancelReason}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Col>
            </Row>

            <div style={{ marginTop: 24 }}>
              <h4>商品清单</h4>
              <Table
                dataSource={selectedOrder.items}
                rowKey="id"
                size="small"
                pagination={false}
                columns={[
                  { title: '商品名称', dataIndex: 'productName', key: 'productName' },
                  { title: '规格', dataIndex: 'spec', key: 'spec' },
                  {
                    title: '单价',
                    dataIndex: 'price',
                    key: 'price',
                    render: (v: number) => `¥${v.toFixed(2)}`,
                  },
                  { title: '数量', dataIndex: 'quantity', key: 'quantity' },
                  {
                    title: '小计',
                    key: 'total',
                    render: (_: any, item: OrderItem) => (
                      <span style={{ color: item.isGift ? '#52c41a' : 'inherit' }}>
                        {item.isGift
                          ? '赠品'
                          : `¥${(item.price * item.quantity).toFixed(2)}`}
                      </span>
                    ),
                  },
                ]}
              />
            </div>

            <div style={{ marginTop: 24, textAlign: 'right' }}>
              <Space>
                {selectedOrder.status === 'paid' && (
                  <Button
                    type="primary"
                    icon={<TruckOutlined />}
                    onClick={() => {
                      setDetailVisible(false)
                      handleDelivery(selectedOrder)
                    }}
                  >
                    确认发货
                  </Button>
                )}
                {selectedOrder.paymentMethod !== 'wechat' &&
                  selectedOrder.paymentStatus === 'pending' && (
                    <Button
                      type="primary"
                      onClick={() => {
                        setDetailVisible(false)
                        setVerifyModalVisible(true)
                      }}
                    >
                      审核支付凭证
                    </Button>
                  )}
                {['pending', 'paid'].includes(selectedOrder.status) && (
                  <Button
                    danger
                    onClick={() => {
                      setDetailVisible(false)
                      setCancelModalVisible(true)
                    }}
                  >
                    取消订单
                  </Button>
                )}
                <Button onClick={() => setDetailVisible(false)}>关闭</Button>
              </Space>
            </div>
          </div>
        )}
      </Modal>

      {/* 取消订单弹窗 */}
      <Modal
        title="取消订单"
        open={cancelModalVisible}
        onOk={handleCancelOrder}
        onCancel={() => {
          setCancelModalVisible(false)
          setCancelReason('')
        }}
        okText="确认取消"
        okType="danger"
      >
        <p>
          确定要取消订单 <strong>{selectedOrder?.orderNumber}</strong> 吗？
        </p>
        <p>客户：{selectedOrder?.customerName}</p>
        <Form layout="vertical">
          <Form.Item label="取消原因" rules={[{ required: true, message: '请输入取消原因' }]}>
            <TextArea
              rows={3}
              placeholder="请输入取消原因"
              value={cancelReason}
              onChange={e => setCancelReason(e.target.value)}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 审核支付凭证弹窗 */}
      <Modal
        title="审核支付凭证"
        open={verifyModalVisible}
        onOk={handleVerifyPayment}
        onCancel={() => setVerifyModalVisible(false)}
        okText="确认审核"
      >
        {selectedOrder?.paymentProof && (
          <div style={{ textAlign: 'center', marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
            {selectedOrder.paymentProof.split(',').filter(Boolean).map((url: string, idx: number) => (
              <Image key={idx} src={getImageUrl(url.trim())} width={200} style={{ borderRadius: 4 }} />
            ))}
          </div>
        )}
        <Form layout="vertical">
          <Form.Item label="审核结果" rules={[{ required: true, message: '请选择审核结果' }]}>
            <Select
              value={verifyResult}
              onChange={setVerifyResult}
              style={{ width: '100%' }}
            >
              <Option value="verified">
                <Tag color="green">
                  <CheckCircleOutlined /> 通过（转为待发货）
                </Tag>
              </Option>
              <Option value="rejected">
                <Tag color="red">
                  <CloseCircleOutlined /> 驳回
                </Tag>
              </Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* 发货弹窗 */}
      <Modal
        title="确认发货"
        open={deliveryModalVisible}
        onOk={handleDeliverySubmit}
        onCancel={() => setDeliveryModalVisible(false)}
        width={500}
        okText="确认发货"
      >
        <p>
          确认对订单 <strong>{deliveringOrder?.orderNumber}</strong> 进行发货？
        </p>
        <p>
          客户：{deliveringOrder?.customerName} | {deliveringOrder?.customerPhone}
        </p>
        <p>地址：{deliveringOrder?.deliveryAddress}</p>
        <Form form={deliveryForm} layout="vertical">
          <Form.Item label="备注（选填）" name="remark">
            <TextArea rows={3} placeholder="选填，如配送时间等备注信息" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default Orders
